import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useCart } from "../context/CartContext";
import Toast from "../components/Toast";

const ContainersWrapper = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--background-dark) 100%
  );
  color: white;
  padding: 120px 0 80px;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 20px;
  color: white;
  font-family: "Inter", sans-serif;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: #e8e8e8;
  max-width: 600px;
  margin: 0 auto;
`;

const FilterSection = styled.section`
  padding: 60px 0;
  background: var(--background-light);
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const FilterButton = styled.button`
  padding: 12px 24px;
  border: 2px solid var(--secondary-color);
  background: ${(props) =>
    props.active ? "var(--secondary-color)" : "transparent"};
  color: ${(props) => (props.active ? "white" : "var(--secondary-color)")};
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--secondary-color);
    color: white;
  }
`;

const ProductsSection = styled.section`
  padding: 80px 0;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const ProductImage = styled.div`
  height: 250px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProductContent = styled.div`
  padding: 30px;

  h3 {
    font-size: 1.25rem;
    color: var(--text-dark);
    margin-bottom: 15px;
  }

  p {
    color: var(--text-light);
    line-height: 1.6;
    margin-bottom: 20px;
  }

  .features {
    list-style: none;
    padding: 0;
    margin-bottom: 20px;

    li {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
      font-size: 0.9rem;
      color: var(--text-light);

      .check {
        color: var(--secondary-color);
        font-weight: bold;
      }
    }
  }
`;

const ContactButton = styled.button`
  width: 100%;
  padding: 12px;
  background: var(--secondary-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #c9a660;
    transform: translateY(-2px);
  }

  &.in-cart {
    background: #10b981;

    &:hover {
      background: #059669;
    }
  }
`;

const ButtonGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 10px;
`;

const AddToCartButton = styled.button`
  padding: 12px;
  background: ${(props) => (props.inCart ? "#10b981" : "var(--primary-color)")};
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.inCart ? "#059669" : "#0c3a63")};
    transform: translateY(-2px);
  }
`;

const ViewDetailsButton = styled.button`
  padding: 12px;
  background: transparent;
  color: var(--secondary-color);
  border: 2px solid var(--secondary-color);
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--secondary-color);
    color: white;
    transform: translateY(-2px);
  }
`;

const Containers = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const navigate = useNavigate();
  const { addToCart, isInCart } = useCart();

  const handleContactClick = () => {
    navigate("/contact");
  };

  const handleAddToCart = (container) => {
    addToCart(container);
    setToastMessage(`${container.title} added to cart!`);
    setShowToast(true);
  };

  const containers = [
    {
      id: 1,
      title: "10FT Shipping Container",
      category: "new",
      image: "/assets/2025/08/10ft-shipping-container-300x300.jpg",
      description: "Perfect for small storage needs and tight spaces.",
      features: [
        "Dimensions: 10' x 8' x 8.5'",
        "Wind and watertight",
        "Steel construction",
        "Cargo doors",
      ],
    },
    {
      id: 2,
      title: "20FT Shipping Container",
      category: "new",
      image: "/assets/2025/08/20ft-shipping-container-red-300x300.jpg",
      description: "Most popular size for residential and commercial use.",
      features: [
        "Dimensions: 20' x 8' x 8.5'",
        "Wind and watertight",
        "Steel construction",
        "Cargo doors",
      ],
    },
    {
      id: 3,
      title: "40FT Shipping Container",
      category: "new",
      image: "/assets/2025/08/40ft-shipping-container-grey-300x300.jpg",
      description: "Maximum storage capacity for large projects.",
      features: [
        "Dimensions: 40' x 8' x 8.5'",
        "Wind and watertight",
        "Steel construction",
        "Cargo doors",
      ],
    },
    {
      id: 4,
      title: "40FT High Cube Container",
      category: "new",
      image: "/assets/2025/08/40ft-high-cube-shipping-container-2.jpg",
      description: "Extra height for oversized cargo and storage needs.",
      features: [
        "Dimensions: 40' x 8' x 9.5'",
        "One foot additional height",
        "Perfect for tall equipment",
        "Wind and watertight",
      ],
    },
    {
      id: 5,
      title: "20FT Used Container",
      category: "used",
      image: "/assets/2025/08/20ft-shipping-container-red-300x300.jpg",
      description: "Cost-effective storage solution in good condition.",
      features: [
        "Dimensions: 20' x 8' x 8.5'",
        "Inspected and certified",
        "Minor wear and tear",
        "Fully functional doors",
      ],
    },
    {
      id: 6,
      title: "40FT Used Container",
      category: "used",
      image: "/assets/2025/08/40ft-shipping-container-grey-300x300.jpg",
      description: "Affordable large-capacity storage solution.",
      features: [
        "Dimensions: 40' x 8' x 8.5'",
        "Inspected for quality",
        "Great value for money",
        "Suitable for long-term storage",
      ],
    },
    {
      id: 7,
      title: "40FT Office Container",
      category: "office",
      image: "/assets/2025/08/40ft-office-container-white-300x300.jpg",
      description: "Complete mobile office solution with all amenities.",
      features: [
        "Insulated and climate controlled",
        "Windows and doors",
        "Electrical system",
        "Flooring installed",
      ],
    },
    {
      id: 8,
      title: "20FT Office Container",
      category: "office",
      image: "/assets/2025/08/20ft-office-container-with-no-a-c-600x450.jpg",
      description: "Compact office space perfect for job sites.",
      features: [
        "Insulated walls and ceiling",
        "Electrical wiring included",
        "Windows for natural light",
        "Ready for immediate use",
      ],
    },
    {
      id: 9,
      title: "30FT Office Container",
      category: "office",
      image: "/assets/2025/08/30ft-office-container-300x300.jpg",
      description: "Mid-size office with bathroom facilities.",
      features: [
        "Includes toilet and sink",
        "HVAC system installed",
        "Multiple workstations",
        "Energy efficient",
      ],
    },
    {
      id: 10,
      title: "20FT Container Home",
      category: "home",
      image: "/assets/2025/08/20ft-container-home-green-300x300.jpg",
      description: "Compact living solution with modern amenities.",
      features: [
        "Fully insulated",
        "Kitchen and bathroom",
        "Electrical and plumbing",
        "Modern finishes",
      ],
    },
    {
      id: 11,
      title: "40FT Luxury Home",
      category: "home",
      image:
        "/assets/2025/08/luxury-farmhouse-style-custom-made-tiny-home-300x300.jpg",
      description: "Premium container home with luxury finishes.",
      features: [
        "High-end finishes",
        "Full kitchen and bath",
        "Premium insulation",
        "Custom design",
      ],
    },
    {
      id: 12,
      title: "20FT Bar Container",
      category: "custom",
      image: "/assets/2025/08/20ft-bar-container-600x450.jpg",
      description: "Mobile bar perfect for events and venues.",
      features: [
        "Custom bar counter",
        "Storage shelving",
        "Electrical hookups",
        "Serving window",
      ],
    },
    {
      id: 13,
      title: "40FT Workshop Container",
      category: "custom",
      image: "/assets/2025/08/20ft-offshore-shipping-container.jpg",
      description: "Fully equipped mobile workshop for professionals.",
      features: [
        "Tool storage systems",
        "Work benches installed",
        "Heavy-duty electrical",
        "Ventilation system",
      ],
    },
    {
      id: 14,
      title: "10FT Storage Unit",
      category: "new",
      image: "/assets/2025/08/10ft-shipping-container-600x450.jpg",
      description: "Ideal for residential backyards and small businesses.",
      features: [
        "Compact footprint",
        "Easy to place",
        "Secure locking system",
        "Weather resistant",
      ],
    },
    {
      id: 15,
      title: "20FT Refrigerated Container",
      category: "custom",
      image: "/assets/2025/08/20ft-shipping-container-red-300x300.jpg",
      description: "Temperature-controlled storage for perishables.",
      features: [
        "Temperature range: -25°C to +25°C",
        "Reliable cooling system",
        "Insulated construction",
        "Digital temperature control",
      ],
    },
  ];

  const filteredContainers =
    activeFilter === "all"
      ? containers
      : containers.filter((container) => container.category === activeFilter);

  return (
    <ContainersWrapper>
      {showToast && (
        <Toast
          title="Added to Cart"
          message={toastMessage}
          type="success"
          onClose={() => setShowToast(false)}
        />
      )}

      <HeroSection>
        <Container>
          <HeroTitle>Our Container Collection</HeroTitle>
          <HeroSubtitle>
            High-quality shipping containers designed for strength, security,
            and versatility
          </HeroSubtitle>
        </Container>
      </HeroSection>

      <FilterSection>
        <Container>
          <FilterContainer>
            <FilterButton
              active={activeFilter === "all"}
              onClick={() => setActiveFilter("all")}
            >
              All Containers
            </FilterButton>
            <FilterButton
              active={activeFilter === "new"}
              onClick={() => setActiveFilter("new")}
            >
              New Containers
            </FilterButton>
            <FilterButton
              active={activeFilter === "used"}
              onClick={() => setActiveFilter("used")}
            >
              Used Containers
            </FilterButton>
            <FilterButton
              active={activeFilter === "office"}
              onClick={() => setActiveFilter("office")}
            >
              Office Containers
            </FilterButton>
            <FilterButton
              active={activeFilter === "home"}
              onClick={() => setActiveFilter("home")}
            >
              Container Homes
            </FilterButton>
            <FilterButton
              active={activeFilter === "custom"}
              onClick={() => setActiveFilter("custom")}
            >
              Custom Solutions
            </FilterButton>
          </FilterContainer>
        </Container>
      </FilterSection>

      <ProductsSection>
        <Container>
          <ProductGrid>
            {filteredContainers.map((container) => (
              <ProductCard key={container.id}>
                <ProductImage
                  onClick={() => navigate(`/containers/${container.id}`)}
                >
                  <img src={container.image} alt={container.title} />
                </ProductImage>
                <ProductContent>
                  <h3
                    onClick={() => navigate(`/containers/${container.id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    {container.title}
                  </h3>
                  <p>{container.description}</p>
                  <ul className="features">
                    {container.features.map((feature, index) => (
                      <li key={index}>
                        <span className="check">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <ButtonGroup>
                    <AddToCartButton
                      inCart={isInCart(container.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToCart(container);
                      }}
                    >
                      {isInCart(container.id) ? (
                        <>
                          <i className="fas fa-check"></i> In Cart
                        </>
                      ) : (
                        <>
                          <i className="fas fa-cart-plus"></i> Add to Cart
                        </>
                      )}
                    </AddToCartButton>
                    <ViewDetailsButton
                      onClick={() => navigate(`/containers/${container.id}`)}
                    >
                      <i className="fas fa-eye"></i> View Details
                    </ViewDetailsButton>
                  </ButtonGroup>
                </ProductContent>
              </ProductCard>
            ))}
          </ProductGrid>
        </Container>
      </ProductsSection>
    </ContainersWrapper>
  );
};

export default Containers;
